import { useEffect, useMemo, useState } from "react";

import {
  listarSesiones,
  listarPacientes,
  crearSesion,
  obtenerPaciente,
  listarEspecialidades,
} from "../api";

import "./AgendarSesion.css";

/**
 * Profesionales reales del centro.
 *
 * IDs:
 * 1  = Administración
 * 2  = Teresa
 * 3  = Mercedes
 * 7  = María
 * 17 = Marina
 * 18 = Marta
 * 19 = Beatriz
 */
const PROFESSIONALS = {
  psicologia: [
    {
      id: 2,
      name: "Teresa",
    },
    {
      id: 17,
      name: "Marina",
    },
    {
      id: 18,
      name: "Marta",
    },
  ],

  logopedia: [
    {
      id: 3,
      name: "Mercedes",
    },
    {
      id: 19,
      name: "Beatriz",
    },
  ],

  nutricion: [
    {
      id: 7,
      name: "María",
    },
  ],

  administracion: [
    {
      id: 1,
      name: "Administración",
    },
  ],
};

/**
 * Servicios disponibles por especialidad.
 */
const SERVICES_BY_SPEC = {
  psicologia: [
    {
      code: "entrevista_inicial",
      label: "Entrevista inicial (60 min) - 60 €",
      duration: 60,
      price: 60,
    },
    {
      code: "evaluacion_psicologica",
      label: "Sesión evaluación psicológica (60 min) - 55 €",
      duration: 60,
      price: 55,
    },
    {
      code: "devolucion_informe_psicologia",
      label: "Devolución informe psicología (60 min) - 55 €",
      duration: 60,
      price: 55,
    },
    {
      code: "sesion_psicologia",
      label: "Sesión psicología (45 min) - 50 €",
      duration: 45,
      price: 50,
    },
     {
      code: "sesion_estimulacion",
      label: "Sesión estimulación (45 min) - 45 €",
      duration: 45,
      price: 45,
    },
     {
      code: "no_disponible",
      label: "no disponible 1h",
      duration: 60,
      price: 0,
    },

    // NEAE: se mantienen sin cambios
    {
      code: "reeducacion_pedagogica_NEAE_mes",
      label: "Reeducación pedagógica NEAE mes (45 min)",
      duration: 45,
      price: 180
    },
    {
      code: "reeducacion_pedagogica_NEAE_sesion",
      label: "Reeducación pedagógica NEAE sesion (45 min)",
      duration: 45,
      price: 45,
    },
    {
      code: "altas_capacidades_NEAE_mes",
      label: "Altas capacidades NEAE mes (45 min)",
      duration: 45,
      price: 180,
    },
    {
      code: "altas_capacidades_NEAE_sesion",
      label: "Altas capacidades NEAE sesion (45 min)",
      duration: 45,
      price: 45,
    },
  ],

  logopedia: [
    {
      code: "entrevista_inicial",
      label: "Entrevista inicial logopedia (60 min) - 50 €",
      duration: 60,
      price: 50,
    },
    {
      code: "evaluacion_logopedica",
      label: "Sesión evaluación logopédica (45 min) - 45 €",
      duration: 45,
      price: 45,
    },
    {
      code: "devolucion_informe_logopedia",
      label: "Devolución informe logopedia (45 min) - 45 €",
      duration: 60,
      price: 45,
    },
    {
      code: "sesion_logopedia",
      label: "Sesión logopedia (45 min) - 40 €",
      duration: 45,
      price: 40,
    },
    {
      code: "no_disponible",
      label: "no disponible 1h",
      duration: 60,
      price: 0,
    },

    // NEAE: se mantienen sin cambios
    {
      code: "intervencion_logopedia_mes",
      label: "Intervención logopedia mensual (45 min)",
      duration: 45,
      price: 160,
    },
    {
      code: "reeducacion_lenguaje_NEAE_mes",
      label: "Reeducación del lenguaje NEAE mes (45 min)",
      duration: 45,
      price: 160,
    },
    {
      code: "reeducacion_lenguaje_NEAE_sesion",
      label: "Reeducación del lenguaje NEAE sesion (45 min)",
      duration: 45,
      price: 40,
    },
  ],

  administracion: [
    {
      code: "gestion_administrativa",
      label: "Gestión administrativa (30 min)",
      duration: 30,
    },
    {
      code: "gestion_beca",
      label: "Gestión beca NEAE (30 min)",
      duration: 30,
    },
    {
      code: "no_disponible",
      label: "no disponible 1h",
      duration: 60,
      price: 0,
    },
  ],

  nutricion: [
    {
      code: "consulta_inicial",
      label: "Consulta inicial (20 min)",
      duration: 20,
    },
    {
      code: "terapia_familiar",
      label: "Terapia familiar (60 min)",
      duration: 60,
    },
    {
      code: "consulta_nutricional",
      label: "Consulta nutricional (45 min)",
      duration: 45,
    },
    {
      code: "no_disponible",
      label: "no disponible 1h",
      duration: 60,
      price: 0,
    },
  ],
};


/**
 * Normaliza las especialidades obtenidas del paciente
 * y elimina duplicados.
 */
function getBaseSpecialtyCode(code) {
  return String(code || "")
    .toLowerCase()
    .trim();
}

function getSpecialtyDisplayName(code, originalName = "") {
  const baseCode = getBaseSpecialtyCode(code);

  if (baseCode === "psicologia") {
    return "Psicología";
  }

  if (baseCode === "logopedia") {
    return "Logopedia";
  }

  if (baseCode === "nutricion") {
    return "Nutrición";
  }

  if (baseCode === "administracion") {
    return "Administración";
  }

  return originalName || code || "";
}

/**
 * Normaliza las especialidades obtenidas del paciente.
 *
 * psicologia-m y psicologia-g pasan a ser psicologia.
 * Además elimina duplicados.
 */
function normalizeSpecialtyList(specialties = []) {
  const normalized = specialties
    .map((specialty) => {
      const originalCode = String(
        specialty?.code || ""
      )
        .toLowerCase()
        .trim();

      const baseCode =
        getBaseSpecialtyCode(originalCode);

      if (!baseCode) {
        return null;
      }

      return {
        ...specialty,
        code: baseCode,
        nombre: getSpecialtyDisplayName(
          baseCode,
          specialty?.nombre ||
            specialty?.name ||
            specialty?.label ||
            originalCode
        ),
      };
    })
    .filter(Boolean);

  return normalized.filter(
    (specialty, index, array) =>
      array.findIndex(
        (item) =>
          item.code === specialty.code
      ) === index
  );
}

function normalize(value) {
  return String(value || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Extrae las especialidades del paciente contemplando
 * los distintos formatos históricos de la API.
 */
function extractPatientSpecialties(
  patient,
  allSpecs
) {
  if (
    Array.isArray(patient?.specialties) &&
    patient.specialties.length
  ) {
    const arr = patient.specialties;

    if (
      typeof arr[0] === "object" &&
      arr[0] !== null
    ) {
      const hasFlag = [
        "selected",
        "checked",
        "asignada",
        "assigned",
        "is_selected",
        "isChecked",
      ].some(
        (key) =>
          key in (arr[0] || {})
      );

      const filtered = hasFlag
        ? arr.filter(
            (item) =>
              item.selected ||
              item.checked ||
              item.asignada ||
              item.assigned ||
              item.is_selected ||
              item.isChecked
          )
        : arr;

      return filtered
        .map((item) => ({
          code: String(
            item.code || ""
          ).trim(),

          nombre: String(
            item.nombre ||
              item.name ||
              item.label ||
              item.code ||
              ""
          ).trim(),
        }))
        .filter(
          (item) => item.code
        );
    }

    return arr
      .map((code) => ({
        code: String(code || "").trim(),
        nombre: String(code || "").trim(),
      }))
      .filter(
        (item) => item.code
      );
  }

  const candidates = [
    patient?.especialidades,
    patient?.especialidades_asignadas,
    patient?.specialty_codes,
    patient?.specialties_codes,
    patient?.specialtyCodes,
    patient?.patient_specialties,
  ].find(
    (value) =>
      Array.isArray(value) &&
      value.length
  );

  if (
    Array.isArray(candidates) &&
    candidates.length
  ) {
    if (
      typeof candidates[0] ===
        "object" &&
      candidates[0] !== null
    ) {
      return candidates
        .map((item) => {
          const obj =
            item.specialty || item;

          return {
            code: String(
              obj.code || ""
            ).trim(),

            nombre: String(
              obj.nombre ||
                obj.name ||
                obj.label ||
                obj.code ||
                ""
            ).trim(),
          };
        })
        .filter(
          (item) => item.code
        );
    }

    return candidates
      .map((code) => ({
        code: String(code || "").trim(),
        nombre: String(code || "").trim(),
      }))
      .filter(
        (item) => item.code
      );
  }

  /**
   * Aquí se mantiene allSpecs sin normalizar.
   *
   * Es importante porque los pacientes antiguos
   * pueden conservar specialty_id 7 u 8.
   */
  if (
    Array.isArray(
      patient?.specialty_ids
    ) &&
    patient.specialty_ids.length &&
    Array.isArray(allSpecs) &&
    allSpecs.length
  ) {
    const result =
      patient.specialty_ids
        .map((id) => {
          const specialty =
            allSpecs.find(
              (item) =>
                Number(item.id) ===
                Number(id)
            );

          if (!specialty) {
            return null;
          }

          return {
            code: String(
              specialty.code || ""
            ).trim(),

            nombre: String(
              specialty.nombre ||
                specialty.code ||
                ""
            ).trim(),
          };
        })
        .filter(Boolean);

    if (result.length) {
      return result;
    }
  }

  const legacyServicios =
    Array.isArray(patient?.servicios)
      ? patient.servicios
      : String(
          patient?.servicios || ""
        )
          .split(",")
          .map((item) =>
            item.trim()
          )
          .filter(Boolean);

  if (legacyServicios.length) {
    if (
      Array.isArray(allSpecs) &&
      allSpecs.length
    ) {
      const result =
        legacyServicios
          .map((code) => {
            const specialty =
              allSpecs.find(
                (item) =>
                  String(
                    item.code || ""
                  )
                    .toLowerCase()
                    .trim() ===
                  String(code || "")
                    .toLowerCase()
                    .trim()
              );

            return specialty
              ? {
                  code:
                    specialty.code,

                  nombre:
                    specialty.nombre ||
                    specialty.code,
                }
              : {
                  code,
                  nombre: code,
                };
          })
          .filter(
            (item) => item.code
          );

      if (result.length) {
        return result;
      }
    }

    return legacyServicios.map(
      (code) => ({
        code,
        nombre: code,
      })
    );
  }

  if (patient?.specialty) {
    const code = String(
      patient.specialty
    ).trim();

    return code
      ? [
          {
            code,
            nombre: code,
          },
        ]
      : [];
  }

  return [];
}

/**
 * Formatea la fecha en el formato esperado por PHP/MySQL.
 */
function formatLocalDateTime(date) {
  const pad = (number) =>
    number < 10
      ? `0${number}`
      : String(number);

  return (
    `${date.getFullYear()}-` +
    `${pad(date.getMonth() + 1)}-` +
    `${pad(date.getDate())} ` +
    `${pad(date.getHours())}:` +
    `${pad(date.getMinutes())}:` +
    `${pad(date.getSeconds())}`
  );
}

export default function AgendarSesion({
  preselectedPatientId,
}) {
  const [patients, setPatients] =
    useState([]);

  const [patientId, setPatientId] =
    useState(
      preselectedPatientId || ""
    );

  const [patientSpec, setPatientSpec] =
    useState("");

  const [
    availableSpecs,
    setAvailableSpecs,
  ] = useState([]);

  const [allSpecs, setAllSpecs] =
    useState([]);

  const [
    professionalId,
    setProfessionalId,
  ] = useState("");

  const [service, setService] =
    useState("");

  const [date, setDate] =
    useState("");

  const [slots, setSlots] =
    useState([]);

  const [
    selectedSlot,
    setSelectedSlot,
  ] = useState(null);

  const [isGroup, setIsGroup] =
    useState(false);

  const [groupSize, setGroupSize] =
    useState(2);

  const [msg, setMsg] =
    useState("");

  const [q, setQ] =
    useState("");

  const [
    loadingSlots,
    setLoadingSlots,
  ] = useState(false);

/**
 * Especialidad clínica seleccionada.
 */
const baseSpecialty =
  getBaseSpecialtyCode(
    patientSpec
  );

  const professionals =
    PROFESSIONALS[
      baseSpecialty
    ] || [];

  const availableServices =
    SERVICES_BY_SPEC[
      baseSpecialty
    ] || [];

  const selectedService =
    availableServices.find(
      (item) =>
        item.code === service
    );

  /**
   * Cargar el catálogo completo de especialidades.
   *
   * Se conserva sin normalizar para poder resolver
   * los IDs antiguos 7 y 8.
   */
  useEffect(() => {
    (async () => {
      try {
        const response =
          await listarEspecialidades();

        const items =
          Array.isArray(response)
            ? response
            : Array.isArray(
                response?.items
              )
            ? response.items
            : [];

        setAllSpecs(items);
      } catch (error) {
        console.error(
          "Error cargando catálogo de especialidades",
          error
        );

        setAllSpecs([]);
      }
    })();
  }, []);

  /**
   * Cargar pacientes.
   */
  useEffect(() => {
    (async () => {
      try {
        const all = [];

        let page = 1;
        let pages = 1;

        do {
          const response =
            await listarPacientes({
              page,
              page_size: 200,
            });

          if (
            response?.items?.length
          ) {
            all.push(
              ...response.items
            );
          }

          pages = Number(
            response?.pages || 1
          );

          page += 1;
        } while (page <= pages);

        setPatients(all);
      } catch (error) {
        console.error(
          "Error cargando pacientes",
          error
        );
      }
    })();
  }, []);

  /**
   * Cargar y normalizar las especialidades
   * asignadas al paciente.
   */
  useEffect(() => {
    (async () => {
      if (!patientId) {
        setAvailableSpecs([]);
        setPatientSpec("");
        setProfessionalId("");
        setService("");
        setSelectedSlot(null);
        setSlots([]);

        return;
      }

      try {
        const patient =
          await obtenerPaciente(
            patientId
          );

        const extractedSpecialties =
          extractPatientSpecialties(
            patient,
            allSpecs
          );

        const specialties =
          normalizeSpecialtyList(
            extractedSpecialties
          );

        setAvailableSpecs(
          specialties
        );

        const firstCode =
          specialties?.[0]?.code ||
          "";

        setPatientSpec(firstCode);
        setProfessionalId("");
        setService("");
        setSelectedSlot(null);
        setSlots([]);
      } catch (error) {
        console.error(
          "Error cargando paciente",
          error
        );

        setAvailableSpecs([]);
        setPatientSpec("");
        setProfessionalId("");
        setService("");
        setSelectedSlot(null);
        setSlots([]);
      }
    })();
  }, [patientId, allSpecs]);

  /**
   * Cuando cambia la especialidad:
   *
   * - Si solo existe una profesional, se asigna.
   * - Si hay varias, debe elegirse.
   */
  useEffect(() => {
    setService("");
    setSelectedSlot(null);
    setSlots([]);
    setMsg("");

    if (
      professionals.length === 1
    ) {
      setProfessionalId(
        String(
          professionals[0].id
        )
      );
    } else {
      setProfessionalId("");
    }
  }, [
    patientSpec,
    professionals.length,
  ]);

  /**
   * Generar huecos y comprobar la ocupación
   * de la profesional seleccionada.
   */
  useEffect(() => {
    if (
      !date ||
      !selectedService ||
      !professionalId
    ) {
      setSlots([]);
      setSelectedSlot(null);

      return;
    }

    let cancelled = false;

    const loadSlots =
      async () => {
        try {
          setLoadingSlots(true);
          setSelectedSlot(null);

          const duration =
            selectedService.duration;

          const stepMinutes =
            duration % 15 === 0
              ? 15
              : 5;

          const generateSlots = (
            startHHMM,
            endHHMM,
            step
          ) => {
            const result = [];

            let cursor =
              new Date(
                `${date}T${startHHMM}:00`
              );

            const blockEnd =
              new Date(
                `${date}T${endHHMM}:00`
              );

            const stepMs =
              step * 60000;

            const durationMs =
              duration * 60000;

            while (
              cursor.getTime() +
                durationMs <=
              blockEnd.getTime()
            ) {
              result.push({
                start: new Date(
                  cursor
                ),

                end: new Date(
                  cursor.getTime() +
                    durationMs
                ),

                occupied: false,
              });

              cursor = new Date(
                cursor.getTime() +
                  stepMs
              );
            }

            return result;
          };

          let generatedSlots =
            generateSlots(
              "10:00",
              "21:00",
              stepMinutes
            );

          /**
           * Solicitamos las sesiones de la fecha.
           *
           * Después se filtran por professional_id,
           * por lo que las agendas no se mezclan.
           */
          const response =
            await listarSesiones({
              date,
            });

          const allOccupied =
            response?.items || [];

          const professionalOccupied =
            allOccupied.filter(
              (session) =>
                Number(
                  session.professional_id
                ) ===
                Number(
                  professionalId
                )
            );

          generatedSlots =
            generatedSlots.map(
              (slot) => {
                const clashes =
                  professionalOccupied.filter(
                    (session) =>
                      slot.start <
                        new Date(
                          session.end_datetime
                        ) &&
                      slot.end >
                        new Date(
                          session.start_datetime
                        )
                  );

                return {
                  ...slot,
                  occupied:
                    clashes.length >
                    0,
                };
              }
            );

          if (!cancelled) {
            setSlots(
              generatedSlots
            );
          }
        } catch (error) {
          console.error(
            "Error calculando huecos",
            error
          );

          if (!cancelled) {
            setSlots([]);

            setMsg(
              "No se pudieron cargar los huecos"
            );
          }
        } finally {
          if (!cancelled) {
            setLoadingSlots(
              false
            );
          }
        }
      };

    loadSlots();

    return () => {
      cancelled = true;
    };
  }, [
    date,
    selectedService,
    professionalId,
    isGroup,
  ]);

  /**
   * Crear una nueva sesión.
   */
  const submit = async () => {
    try {
      setMsg("");

      if (
        !patientId ||
        !baseSpecialty ||
        !professionalId ||
        !service ||
        !selectedSlot
      ) {
        setMsg(
          "Selecciona paciente, especialidad, profesional, servicio y horario"
        );

        return;
      }

      if (!selectedService) {
        setMsg(
          "Servicio inválido para esa especialidad"
        );

        return;
      }

      /**
       * Comprobar nuevamente la agenda justo
       * antes de guardar.
       */
      const response =
        await listarSesiones({
          date,
        });

      const occupied = (
        response?.items || []
      ).filter(
        (session) =>
          Number(
            session.professional_id
          ) ===
          Number(professionalId)
      );

      const clashes =
        occupied.filter(
          (session) =>
            selectedSlot.start <
              new Date(
                session.end_datetime
              ) &&
            selectedSlot.end >
              new Date(
                session.start_datetime
              )
        );

      const hasIndividual =
        clashes.some(
          (session) =>
            !session.is_group
        );

      if (
        hasIndividual &&
        !isGroup
      ) {
        setMsg(
          "Ese horario ya está ocupado para la profesional seleccionada ❌"
        );

        return;
      }

      await crearSesion({
        patient_id:
          Number(patientId),

        /**
         * Siempre se guarda la especialidad clínica.
         *
         * Para Teresa, Marina y Ana:
         * specialty = psicologia
         *
         * La profesional concreta queda identificada
         * mediante professional_id.
         */
        specialty:
          baseSpecialty,

        professional_id:
          Number(professionalId),

        service_code:
          service,

        start_datetime:
          formatLocalDateTime(
            selectedSlot.start
          ),

        end_datetime:
          formatLocalDateTime(
            selectedSlot.end
          ),

        duration_minutes:
          selectedService.duration,

        is_group:
          isGroup ? 1 : 0,

        group_size:
          isGroup
            ? Number(groupSize)
            : null,
      });

      setMsg(
        "Sesión agendada ✅"
      );

      /**
       * Guardamos la referencia antes de eliminar
       * selectedSlot del estado.
       */
      const createdSlot =
        selectedSlot;

      setSelectedSlot(null);

      setSlots((current) =>
        current.map((slot) => {
          const overlaps =
            slot.start <
              createdSlot.end &&
            slot.end >
              createdSlot.start;

          return overlaps
            ? {
                ...slot,
                occupied: true,
              }
            : slot;
        })
      );
    } catch (error) {
      console.error(
        "Error creando sesión",
        error
      );

      setMsg(
        `Error: ${
          error?.message ||
          "Error desconocido"
        }`
      );
    }
  };

  /**
   * Buscar pacientes por nombre.
   */
  const filteredPatients =
    useMemo(() => {
      const normalizedQuery =
        normalize(q);

      if (!normalizedQuery) {
        return patients;
      }

      const tokens =
        normalizedQuery
          .split(" ")
          .filter(Boolean);

      return patients.filter(
        (patient) => {
          const normalizedName =
            normalize(
              patient.nombre
            );

          return tokens.every(
            (token) =>
              normalizedName.includes(
                token
              )
          );
        }
      );
    }, [patients, q]);

  return (
    <div className="agenda-sesion">
      <h2>Agendar sesión</h2>

      <div className="form-row">
        <label>Paciente</label>

        <input
          type="text"
          value={q}
          onChange={(event) =>
            setQ(
              event.target.value
            )
          }
          placeholder="Buscar paciente..."
        />

        <select
          value={patientId}
          onChange={(event) =>
            setPatientId(
              event.target.value
            )
          }
        >
          <option value="">
            -- Seleccionar --
          </option>

          {filteredPatients.map(
            (patient) => (
              <option
                key={patient.id}
                value={patient.id}
              >
                {patient.nombre}
              </option>
            )
          )}
        </select>
      </div>

      {patientId && (
        <>
          <div className="form-row">
            <label>
              Especialidad
            </label>

            {availableSpecs.length >
            1 ? (
              <select
                value={patientSpec}
                onChange={(event) =>
                  setPatientSpec(
                    event.target
                      .value
                  )
                }
              >
                {availableSpecs.map(
                  (specialty) => (
                    <option
                      key={
                        specialty.code
                      }
                      value={
                        specialty.code
                      }
                    >
                      {specialty.nombre ||
                        specialty.code}
                    </option>
                  )
                )}
              </select>
            ) : availableSpecs.length ===
              1 ? (
              <input
                type="text"
                value={
                  availableSpecs[0]
                    .nombre ||
                  availableSpecs[0]
                    .code
                }
                disabled
              />
            ) : (
              <p
                style={{
                  margin: 0,
                }}
              >
                Este paciente no
                tiene especialidades
                asignadas ❌
              </p>
            )}
          </div>

          {patientSpec && (
            <>
              <div className="form-row">
                <label>
                  Profesional
                </label>

                {professionals.length >
                1 ? (
                  <select
                    value={
                      professionalId
                    }
                    onChange={(
                      event
                    ) => {
                      setProfessionalId(
                        event.target
                          .value
                      );

                      setSelectedSlot(
                        null
                      );
                    }}
                  >
                    <option value="">
                      -- Seleccionar
                      profesional --
                    </option>

                    {professionals.map(
                      (
                        professional
                      ) => (
                        <option
                          key={
                            professional.id
                          }
                          value={
                            professional.id
                          }
                        >
                          {
                            professional.name
                          }
                        </option>
                      )
                    )}
                  </select>
                ) : professionals.length ===
                  1 ? (
                  <input
                    type="text"
                    value={
                      professionals[0]
                        .name
                    }
                    disabled
                  />
                ) : (
                  <p
                    style={{
                      margin: 0,
                    }}
                  >
                    No hay
                    profesionales
                    configuradas para
                    esta especialidad.
                  </p>
                )}
              </div>

              <div className="form-row">
                <label>
                  Servicio
                </label>

                <select
                  value={service}
                  onChange={(
                    event
                  ) => {
                    setService(
                      event.target
                        .value
                    );

                    setSelectedSlot(
                      null
                    );
                  }}
                >
                  <option value="">
                    -- Seleccionar --
                  </option>

                  {availableServices.map(
                    (item) => (
                      <option
                        key={
                          item.code
                        }
                        value={
                          item.code
                        }
                      >
                        {item.label}
                      </option>
                    )
                  )}
                </select>
              </div>

              <div className="form-row">
                <label>Fecha</label>

                <input
                  type="date"
                  value={date}
                  onChange={(
                    event
                  ) => {
                    setDate(
                      event.target
                        .value
                    );

                    setSelectedSlot(
                      null
                    );
                  }}
                />
              </div>

              <div className="slots-panel">
                {loadingSlots && (
                  <p>
                    Cargando huecos…
                  </p>
                )}

                {!loadingSlots &&
                  slots.length ===
                    0 && (
                    <p>
                      Selecciona
                      profesional,
                      fecha y servicio.
                    </p>
                  )}

                {!loadingSlots &&
                  slots.map(
                    (
                      slot,
                      index
                    ) => (
                      <button
                        key={`${slot.start.toISOString()}-${index}`}
                        type="button"
                        className={[
                          "slot-bubble",

                          slot.occupied
                            ? "occupied"
                            : "",

                          selectedSlot ===
                          slot
                            ? "selected"
                            : "",
                        ]
                          .filter(
                            Boolean
                          )
                          .join(" ")}
                        disabled={
                          slot.occupied
                        }
                        aria-disabled={
                          slot.occupied
                        }
                        onClick={() => {
                          if (
                            slot.occupied
                          ) {
                            return;
                          }

                          setSelectedSlot(
                            slot
                          );
                        }}
                      >
                        {slot.start.toLocaleTimeString(
                          [],
                          {
                            hour: "2-digit",
                            minute:
                              "2-digit",
                          }
                        )}

                        {" - "}

                        {slot.end.toLocaleTimeString(
                          [],
                          {
                            hour: "2-digit",
                            minute:
                              "2-digit",
                          }
                        )}
                      </button>
                    )
                  )}
              </div>

              <div className="form-row">
                <label>
                  <input
                    type="checkbox"
                    checked={
                      isGroup
                    }
                    onChange={(
                      event
                    ) =>
                      setIsGroup(
                        event.target
                          .checked
                      )
                    }
                  />

                  Sesión múltiple
                </label>

                {isGroup && (
                  <input
                    type="number"
                    min="2"
                    max="5"
                    value={
                      groupSize
                    }
                    onChange={(
                      event
                    ) =>
                      setGroupSize(
                        event.target
                          .value
                      )
                    }
                  />
                )}
              </div>
            </>
          )}
        </>
      )}

      <button
        type="button"
        onClick={submit}
        disabled={
          !patientId ||
          !baseSpecialty ||
          !professionalId ||
          !service ||
          !selectedSlot
        }
      >
        Agendar
      </button>

      {msg && <p>{msg}</p>}
    </div>
  );
}